import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Loader2, Users, IndianRupee, ShieldCheck, Receipt, FileText, ScrollText, Settings,
  KeyRound, Ban, CheckCircle2, Download, Printer, X, Search, UserRound, Landmark } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../../lib/api";
import { PageHeader, StatCard, StatusBadge, LoadingRows, TableShell, TR, TD, EmptyState, ConfirmDialog } from "../../../components/Shared";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Switch } from "../../../components/ui/switch";

const inr2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ROLES = [["DIRECTOR", "Director"], ["EXECUTIVE_DIRECTOR", "Executive Director"], ["MANAGER", "Manager"], ["EXECUTIVE_MANAGER", "Executive Manager"], ["CLIENT", "Client"]];
const METHODS = ["PhonePe", "Google Pay", "UPI", "Bank Transfer", "NEFT", "IMPS", "Manual / Cash", "Other"];
const PAY_STATUS = ["PENDING", "VERIFIED", "REJECTED", "REVERSED"];

const EMPTY_MEMBER = { full_name: "", email: "", password: "", role: "CLIENT", mobile: "", whatsapp: "", joining_date: "", closing_date: "",
  rank: "", district: "", state: "", pincode: "", address: "", investment_amount: "", client_id: "", referral_id: "", manager_id: "", aadhaar: "", pan: "",
  bank: { holder: "", account_number: "", bank_name: "", branch: "", ifsc: "", micr: "" } };

const F = ({ label, value, onChange, type = "text", ph, testid }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={ph} data-testid={testid} />
  </div>
);

function MemberForm({ form, setForm, staff, isEdit }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setBank = (k, v) => setForm({ ...form, bank: { ...form.bank, [k]: v } });
  return (
    <div className="space-y-5 max-h-[62vh] overflow-y-auto pr-1">
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Full Name *" value={form.full_name} onChange={(v) => set("full_name", v)} testid="mf-name" />
        <F label="Email *" value={form.email} onChange={(v) => set("email", v)} type="email" testid="mf-email" />
        {!isEdit && <F label="Password * (min 6)" value={form.password} onChange={(v) => set("password", v)} type="password" testid="mf-password" />}
        <div className="space-y-1"><Label className="text-xs">Role *</Label>
          <Select value={form.role} onValueChange={(v) => set("role", v)}>
            <SelectTrigger data-testid="mf-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
        <F label="Mobile Number" value={form.mobile} onChange={(v) => set("mobile", v)} testid="mf-mobile" />
        <F label="WhatsApp Number" value={form.whatsapp} onChange={(v) => set("whatsapp", v)} testid="mf-whatsapp" />
        <F label="Joining Date" value={form.joining_date} onChange={(v) => set("joining_date", v)} type="date" testid="mf-joining" />
        <F label="Closing Date" value={form.closing_date} onChange={(v) => set("closing_date", v)} type="date" testid="mf-closing" />
        <F label="Rank" value={form.rank} onChange={(v) => set("rank", v)} testid="mf-rank" />
        <F label="District" value={form.district} onChange={(v) => set("district", v)} testid="mf-district" />
        <F label="State" value={form.state} onChange={(v) => set("state", v)} testid="mf-state" />
        <F label="PIN Code" value={form.pincode} onChange={(v) => set("pincode", v)} testid="mf-pincode" />
        {form.role === "CLIENT" && <>
          <F label="Client ID (auto if empty)" value={form.client_id} onChange={(v) => set("client_id", v)} testid="mf-clientid" />
          <F label="Referral ID (auto if empty)" value={form.referral_id} onChange={(v) => set("referral_id", v)} testid="mf-refid" />
          <div className="space-y-1"><Label className="text-xs">Assigned Manager</Label>
            <Select value={form.manager_id || "none"} onValueChange={(v) => set("manager_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="mf-manager"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.role_label}</SelectItem>)}</SelectContent>
            </Select></div>
          <F label="Investment Amount (₹)" value={form.investment_amount} onChange={(v) => set("investment_amount", v)} type="number" testid="mf-investment" />
        </>}
      </div>
      <div className="space-y-1"><Label className="text-xs">Address</Label>
        <Textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="mf-address" /></div>
      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5" /> Bank & Identity (sensitive — masked for non-owners)</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Aadhaar Number (12 digits)" value={form.aadhaar} onChange={(v) => set("aadhaar", v)} testid="mf-aadhaar" />
          <F label="PAN Number" value={form.pan} onChange={(v) => set("pan", v)} ph="ABCDE1234F" testid="mf-pan" />
          <F label="Account Holder Name" value={form.bank.holder} onChange={(v) => setBank("holder", v)} testid="mf-bank-holder" />
          <F label="Account Number" value={form.bank.account_number} onChange={(v) => setBank("account_number", v)} testid="mf-bank-acc" />
          <F label="Bank Name" value={form.bank.bank_name} onChange={(v) => setBank("bank_name", v)} testid="mf-bank-name" />
          <F label="Branch Name" value={form.bank.branch} onChange={(v) => setBank("branch", v)} testid="mf-bank-branch" />
          <F label="IFSC Code" value={form.bank.ifsc} onChange={(v) => setBank("ifsc", v)} testid="mf-bank-ifsc" />
          <F label="MICR Code" value={form.bank.micr} onChange={(v) => setBank("micr", v)} testid="mf-bank-micr" />
        </div>
      </div>
    </div>
  );
}

export function StatementView({ rows, summary, testid }) {
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {[["Investment", summary.investment_amount], ["Opening Balance", summary.opening_balance],
          ["Verified (Period)", summary.period_verified], ["Total Verified", summary.total_verified],
          ["Outstanding", summary.outstanding]].map(([l, v]) => (
          <div key={l} className="bg-card border border-border rounded-xl p-3.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{l}</p>
            <p className={`font-display font-extrabold mt-1 ${l === "Outstanding" && v > 0 ? "text-amber-600" : ""}`}>{inr2(v)}</p>
          </div>
        ))}
      </div>
      <TableShell head={["S.No", "Payment Date", "Payment Time", "Description", "Method", "Recorded By", "Amount", "Status", "Remarks"]} testid={`${testid}-table`}>
        {rows.length === 0 && <TR><TD><span className="text-sm text-muted-foreground">No transactions in this period.</span></TD><TD /><TD /><TD /><TD /><TD /><TD /><TD /></TR>}
        {rows.map((p, i) => (
          <TR key={p.id} testid={`stmt-row-${p.id}`}>
            <TD>{i + 1}</TD>
            <TD><span className="text-xs font-semibold">{p.payment_date}</span></TD>
            <TD><span className="text-xs">{p.payment_time}</span></TD>
            <TD><span className="text-xs max-w-[130px] truncate block">{p.description || "—"}</span>{p.txn_ref && <span className="text-[10px] font-mono text-muted-foreground">{p.txn_ref}</span>}</TD>
            <TD><span className="text-xs font-semibold">{p.method}</span></TD>
            <TD><span className="text-xs text-muted-foreground">{p.recorded_by?.name || "—"}</span></TD>
            <TD><span className="font-bold">{inr2(p.amount)}</span>{p.direction === "sent" && <span className="text-[10px] text-red-500 ml-1">(sent)</span>}</TD>
            <TD><StatusBadge status={p.status.toLowerCase()} /></TD>
            <TD><span className="text-xs max-w-[140px] truncate block">{p.remarks || "—"}</span></TD>
          </TR>
        ))}
      </TableShell>
    </div>
  );
}

export function StatementFilters({ f, setF, onApply, testid }) {
  const years = []; const cy = new Date().getFullYear();
  for (let y = cy - 4; y <= cy + 1; y++) years.push(y);
  const fys = years.map((y) => `${y}-${String(y + 1).slice(2)}`);
  return (
    <div className="flex flex-wrap gap-2 items-end mb-4" data-testid={testid}>
      <div className="space-y-1"><Label className="text-xs">Period</Label>
        <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
          <SelectTrigger className="w-36" data-testid={`${testid}-mode`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem><SelectItem value="month">Month</SelectItem>
            <SelectItem value="fy">Financial Year</SelectItem><SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select></div>
      {f.mode === "month" && <>
        <div className="space-y-1"><Label className="text-xs">Month</Label>
          <Select value={String(f.month)} onValueChange={(v) => setF({ ...f, month: Number(v) })}>
            <SelectTrigger className="w-32" data-testid={`${testid}-month`}><SelectValue /></SelectTrigger>
            <SelectContent>{["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><Label className="text-xs">Year</Label>
          <Select value={String(f.year)} onValueChange={(v) => setF({ ...f, year: Number(v) })}>
            <SelectTrigger className="w-24" data-testid={`${testid}-year`}><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select></div>
      </>}
      {f.mode === "fy" && (
        <div className="space-y-1"><Label className="text-xs">Financial Year</Label>
          <Select value={f.fy} onValueChange={(v) => setF({ ...f, fy: v })}>
            <SelectTrigger className="w-32" data-testid={`${testid}-fy`}><SelectValue /></SelectTrigger>
            <SelectContent>{fys.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
          </Select></div>)}
      {f.mode === "custom" && <>
        <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} data-testid={`${testid}-from`} /></div>
        <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid={`${testid}-to`} /></div>
      </>}
      <div className="space-y-1"><Label className="text-xs">Method</Label>
        <Select value={f.method || "all"} onValueChange={(v) => setF({ ...f, method: v === "all" ? "" : v })}>
          <SelectTrigger className="w-36" data-testid={`${testid}-method`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Methods</SelectItem>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select></div>
      <div className="space-y-1"><Label className="text-xs">Status</Label>
        <Select value={f.status || "all"} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-32" data-testid={`${testid}-status`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem>{PAY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select></div>
      <Button onClick={onApply} className="rounded-full font-bold gradient-bg text-white border-0" data-testid={`${testid}-apply`}>Apply</Button>
    </div>
  );
}

export const stmtQS = (f) => {
  const p = new URLSearchParams();
  if (f.mode === "month") { p.set("month", f.month); p.set("year", f.year); }
  if (f.mode === "fy") p.set("fy", f.fy);
  if (f.mode === "custom" && f.from && f.to) { p.set("date_from", f.from); p.set("date_to", f.to); }
  if (f.method) p.set("method", f.method);
  if (f.status) p.set("status", f.status);
  return p.toString();
};

export const printStatement = (siteName, summary, rows) => {
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>Statement — ${esc(summary.member_name)}</title><style>
    body{font-family:Arial,sans-serif;padding:24px;color:#111} h1{font-size:18px} table{width:100%;border-collapse:collapse;font-size:12px;margin-top:12px}
    th{background:#0F172A;color:#fff;text-align:left} th,td{border:1px solid #ccc;padding:6px 8px} .sum{display:flex;gap:18px;flex-wrap:wrap;margin:12px 0;font-size:13px}
    </style></head><body>
    <h1>${esc(siteName)} — Payment Statement</h1>
    <p>Client: <b>${esc(summary.member_name)}</b> (${esc(summary.client_id || "—")}) · Period: ${esc(summary.period)}</p>
    <div class="sum"><span>Investment: <b>${inr2(summary.investment_amount)}</b></span><span>Opening: <b>${inr2(summary.opening_balance)}</b></span>
    <span>Verified (period): <b>${inr2(summary.period_verified)}</b></span><span>Total verified: <b>${inr2(summary.total_verified)}</b></span>
    <span>Outstanding: <b>${inr2(summary.outstanding)}</b></span></div>
    <table><tr><th>S.No</th><th>Payment Date</th><th>Time</th><th>Description</th><th>Method</th><th>Recorded By</th><th>Amount</th><th>Status</th><th>Remarks</th></tr>
    ${rows.map((p, i) => `<tr><td>${i + 1}</td><td>${esc(p.payment_date)}</td><td>${esc(p.payment_time)}</td><td>${esc(p.description || p.txn_ref || "—")}</td><td>${esc(p.method)}</td><td>${esc(p.recorded_by?.name || "—")}</td><td>${inr2(p.amount)}</td><td>${esc(p.status)}</td><td>${esc(p.remarks || "")}</td></tr>`).join("")}
    </table><p style="font-size:11px;color:#666;margin-top:10px">Generated ${new Date().toLocaleString("en-IN")} · MILTROO Real Estate</p>
    </body></html>`);
  w.document.close();
  w.print();
};

export default function ReOrgPanel() {
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [members, setMembers] = useState(null);
  const [staff, setStaff] = useState([]);
  const [payments, setPayments] = useState(null);
  const [perms, setPerms] = useState(null);
  const [logs, setLogs] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [payForm, setPayForm] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [pq, setPq] = useState("");
  const [pflt, setPflt] = useState({ method: "", status: "", from: "", to: "" });
  const [roleF, setRoleF] = useState("all");
  const [stmtMember, setStmtMember] = useState("");
  const [stmt, setStmt] = useState(null);
  const [sf, setSf] = useState({ mode: "month", month: new Date().getMonth() + 1, year: new Date().getFullYear(), fy: `${new Date().getFullYear()}-${String(new Date().getFullYear() + 1).slice(2)}`, from: "", to: "", method: "", status: "" });

  const loadOv = () => api.get("/re/owner/org/overview").then((r) => setOv(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadMembers = () => api.get("/re/owner/org/members").then((r) => { setMembers(r.data.members); setStaff(r.data.members.filter((m) => m.role !== "CLIENT")); }).catch(() => setMembers([]));
  const loadPayments = (f) => {
    const flt = f || pflt;
    const p = new URLSearchParams();
    if (flt.method) p.set("method", flt.method);
    if (flt.status) p.set("status", flt.status);
    if (flt.from) p.set("date_from", flt.from);
    if (flt.to) p.set("date_to", flt.to);
    api.get(`/re/owner/org/payments?${p}`).then((r) => setPayments(r.data.payments)).catch(() => setPayments([]));
  };
  const loadPerms = () => api.get("/re/owner/org/permissions").then((r) => setPerms(r.data)).catch(() => {});
  const loadLogs = () => api.get("/re/owner/org/audit").then((r) => setLogs(r.data.logs)).catch(() => setLogs([]));
  useEffect(() => { loadOv(); loadMembers(); loadPayments(); loadPerms(); loadLogs(); }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); loadOv(); loadMembers(); loadPayments(); loadLogs(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const openEdit = (m) => {
    setForm({ ...EMPTY_MEMBER, ...m, password: "", bank: { ...EMPTY_MEMBER.bank, ...(m.bank || {}) } });
    setDlg({ mode: "member", id: m.id });
  };
  const saveMember = () => act(async () => {
    const payload = { ...form }; delete payload.password;
    if (dlg.id) await api.patch(`/re/owner/org/members/${dlg.id}`, payload);
    else await api.post("/re/owner/org/members", form);
    setDlg(null);
  }, dlg?.id ? "Member updated" : "Member created");

  const savePayment = () => act(async () => {
    if (payForm.id) await api.patch(`/re/owner/org/payments/${payForm.id}`, payForm);
    else await api.post("/re/owner/org/payments", payForm);
    setPayForm(null);
  }, payForm?.id ? "Payment updated" : "Payment recorded (pending verification)");

  const loadStatement = (mid = stmtMember, filters = sf) => {
    if (!mid) return;
    api.get(`/re/owner/org/members/${mid}/statement?${stmtQS(filters)}`).then((r) => setStmt(r.data)).catch((e) => toast.error(errMsg(e)));
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/re/owner/org/members/${stmtMember}/statement.pdf?${stmtQS(sf)}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF export failed"); }
  };

  const filtered = useMemo(() => (members || []).filter((m) =>
    (roleF === "all" || m.role === roleF) &&
    (!q || [m.full_name, m.email, m.mobile, m.client_id, m.referral_id].join(" ").toLowerCase().includes(q.toLowerCase()))), [members, q, roleF]);
  const payShown = useMemo(() => (payments || []).filter((p) => !pq || [p.member_name, p.client_id, p.txn_ref].join(" ").toLowerCase().includes(pq.toLowerCase())), [payments, pq]);

  const TABS = [["overview", "Overview", ShieldCheck], ["users", "User Management", Users], ["roles", "Roles & Permissions", KeyRound],
    ["payments", "Payments", Receipt], ["statements", "Payment Statements", FileText], ["audit", "Audit Logs", ScrollText]];

  return (
    <div data-testid="re-org-panel">
      <PageHeader title="Real Estate Org Panel" subtitle="Users, roles, clients, payments & statements for your real estate website" testid="re-org-title">
        <Button data-testid="re-org-add-user" onClick={() => { setForm(EMPTY_MEMBER); setDlg({ mode: "member" }); }}
          className="rounded-full font-bold gradient-bg text-white border-0 glow-sm"><Plus className="h-4 w-4 mr-1.5" />Add User</Button>
      </PageHeader>

      <div className="flex gap-2 overflow-x-auto pb-1 mb-6">
        {TABS.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`re-org-tab-${k}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${tab === k ? "gradient-bg text-white shadow-lg" : "bg-card border border-border hover:bg-muted"}`}>
            <I className="h-3.5 w-3.5" />{l}</button>
        ))}
      </div>

      {tab === "overview" && (!ov ? <LoadingRows rows={3} /> : (
        <div className="space-y-6" data-testid="re-org-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Total Users" value={ov.counts.total || 0} testid="ov-users" />
            <StatCard icon={UserRound} label="Directors / Exec Directors" value={(ov.counts.DIRECTOR || 0) + (ov.counts.EXECUTIVE_DIRECTOR || 0)} testid="ov-directors" delay={0.03} />
            <StatCard icon={UserRound} label="Managers / Exec Managers" value={(ov.counts.MANAGER || 0) + (ov.counts.EXECUTIVE_MANAGER || 0)} testid="ov-managers" delay={0.06} />
            <StatCard icon={Users} label="Clients" value={ov.counts.CLIENT || 0} testid="ov-clients" delay={0.09} tint="bg-blue-500/10 text-blue-500" />
            <StatCard icon={IndianRupee} label="Total Investment" value={inr2(ov.counts.investment || 0)} testid="ov-investment" delay={0.12} tint="bg-violet-500/10 text-violet-500" />
            <StatCard icon={IndianRupee} label="Payments Received (Verified)" value={inr2(ov.verified_received)} testid="ov-received" delay={0.15} tint="bg-emerald-500/10 text-emerald-500" />
            <StatCard icon={IndianRupee} label="Outstanding Balance" value={inr2(ov.outstanding)} testid="ov-outstanding" delay={0.18} tint="bg-amber-500/10 text-amber-500" />
            <StatCard icon={IndianRupee} label="This Month Received" value={inr2(ov.month_received)} testid="ov-month" delay={0.21} />
            <StatCard icon={Receipt} label="Pending Verifications" value={ov.pending_count} sub={inr2(ov.pending_amount)} testid="ov-pending" delay={0.24} tint="bg-red-500/10 text-red-500" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg mb-3">Recently Added Users</h2>
            <TableShell head={["Name", "Role", "Client ID", "Mobile", "Joined", "Status"]} testid="ov-recent-table">
              {(ov.recent_members || []).map((m) => (
                <TR key={m.id} testid={`ov-member-${m.id}`}>
                  <TD><span className="font-semibold text-sm">{m.full_name}</span></TD>
                  <TD><span className="text-xs">{m.role_label}</span></TD>
                  <TD><span className="text-xs font-mono">{m.client_id || "—"}</span></TD>
                  <TD><span className="text-xs">{m.mobile || "—"}</span></TD>
                  <TD><span className="text-xs text-muted-foreground">{m.joining_date || fmtDateTime(m.created_at)}</span></TD>
                  <TD><StatusBadge status={m.status} /></TD>
                </TR>
              ))}
            </TableShell>
          </div>
        </div>
      ))}

      {tab === "users" && (
        <div className="space-y-4" data-testid="re-org-users">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, mobile, Client ID…" className="pl-9" data-testid="re-org-search" /></div>
            <Select value={roleF} onValueChange={setRoleF}>
              <SelectTrigger className="w-44" data-testid="re-org-rolefilter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Roles</SelectItem>{ROLES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!members ? <LoadingRows rows={4} /> : filtered.length === 0 ? <EmptyState title="No users found" body="Add your first Director, Manager or Client." icon={Users} /> : (
            <TableShell head={["Name", "Role", "Client / Referral ID", "Contact", "Manager", "Investment", "Status", "Actions"]} testid="re-org-users-table">
              {filtered.map((m) => (
                <TR key={m.id} testid={`re-org-user-${m.id}`}>
                  <TD><p className="font-semibold text-sm">{m.full_name}</p><p className="text-[10px] text-muted-foreground">{m.email}</p></TD>
                  <TD><span className="text-xs font-bold">{m.role_label}</span></TD>
                  <TD><span className="text-[11px] font-mono">{m.client_id || "—"}{m.referral_id ? ` · ${m.referral_id}` : ""}</span></TD>
                  <TD><span className="text-xs">{m.mobile || "—"}</span></TD>
                  <TD><span className="text-xs">{staff.find((s) => s.id === m.manager_id)?.full_name || "—"}</span></TD>
                  <TD><span className="font-bold text-xs">{m.role === "CLIENT" ? inr2(m.investment_amount) : "—"}</span></TD>
                  <TD><StatusBadge status={m.status} />{m.has_pending && <span className="ml-1.5 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600" title="Profile changes awaiting approval">PENDING</span>}</TD>
                  <TD>
                    <div className="flex gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="rounded-full font-bold" data-testid={`re-org-view-${m.id}`} onClick={() => setDlg({ mode: "detail", id: m.id })}>View</Button>
                      <Button size="sm" variant="ghost" data-testid={`re-org-edit-${m.id}`} onClick={() => openEdit(m)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title={m.status === "active" ? "Suspend" : "Activate"} data-testid={`re-org-toggle-${m.id}`}
                        onClick={() => act(() => api.patch(`/re/owner/org/members/${m.id}`, { status: m.status === "active" ? "suspended" : "active" }), m.status === "active" ? "Account suspended" : "Account activated")}>
                        {m.status === "active" ? <Ban className="h-4 w-4 text-red-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}</Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}

      {tab === "roles" && (!perms ? <LoadingRows rows={3} /> : (
        <div className="space-y-6" data-testid="re-org-roles">
          <div className="bg-card border border-border rounded-2xl p-6 overflow-x-auto">
            <p className="font-display font-bold text-sm mb-1">Role Permissions</p>
            <p className="text-xs text-muted-foreground mb-4">Payment <b>verification</b> is always owner-only. Managers/Executive Managers see only their assigned clients.</p>
            <table className="w-full text-sm min-w-[640px]">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-4">Permission</th>{ROLES.map(([k, l]) => <th key={k} className="py-2 pr-3">{l}</th>)}</tr></thead>
              <tbody>
                {perms.catalog.map((p) => (
                  <tr key={p} className="border-t border-border">
                    <td className="py-2.5 pr-4 text-xs font-semibold">{p.replace(/_/g, " ")}</td>
                    {ROLES.map(([r]) => (
                      <td key={r} className="py-2.5 pr-3">
                        <Switch data-testid={`perm-${r}-${p}`}
                          checked={(perms.permissions[r] || []).includes(p)}
                          disabled={r === "CLIENT" && !["notifications"].includes(p)}
                          onCheckedChange={(v) => setPerms({ ...perms, permissions: { ...perms.permissions, [r]: v ? [...(perms.permissions[r] || []), p] : (perms.permissions[r] || []).filter((x) => x !== p) } })} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6">
            <p className="font-display font-bold text-sm mb-1">Client Self-Edit Fields</p>
            <p className="text-xs text-muted-foreground mb-4">Clients may edit only these profile fields themselves. All other fields are admin-managed.</p>
            <div className="flex flex-wrap gap-4">
              {perms.client_editable_catalog.map((f) => (
                <label key={f} className="flex items-center gap-2 text-xs font-bold" data-testid={`cef-${f}`}>
                  <Switch checked={(perms.client_edit_fields || []).includes(f)}
                    onCheckedChange={(v) => setPerms({ ...perms, client_edit_fields: v ? [...perms.client_edit_fields, f] : perms.client_edit_fields.filter((x) => x !== f) })} />
                  {f.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6">
            <p className="font-display font-bold text-sm mb-1">Require Admin Approval For</p>
            <p className="text-xs text-muted-foreground mb-4">Client edits to these fields are submitted for your approval instead of applying immediately.</p>
            <div className="flex flex-wrap gap-4">
              {perms.client_editable_catalog.map((f) => (
                <label key={f} className="flex items-center gap-2 text-xs font-bold" data-testid={`apf-${f}`}>
                  <Switch checked={(perms.approval_fields || []).includes(f)}
                    onCheckedChange={(v) => setPerms({ ...perms, approval_fields: v ? [...(perms.approval_fields || []), f] : (perms.approval_fields || []).filter((x) => x !== f) })} />
                  {f.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          </div>
          <Button data-testid="re-org-save-perms" disabled={busy} className="rounded-full font-bold gradient-bg text-white border-0"
            onClick={() => act(() => api.put("/re/owner/org/permissions", { permissions: perms.permissions, client_edit_fields: perms.client_edit_fields, approval_fields: perms.approval_fields || [] }), "Permissions saved")}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Permissions"}</Button>
        </div>
      ))}

      {tab === "payments" && (
        <div className="space-y-4" data-testid="re-org-payments">
          <div className="flex justify-end">
            <Button data-testid="re-org-add-payment" onClick={() => setPayForm({ member_id: "", amount: "", direction: "received", payment_date: new Date().toISOString().slice(0, 10), payment_time: new Date().toTimeString().slice(0, 5), method: "UPI", txn_ref: "", description: "", remarks: "" })}
              className="rounded-full font-bold gradient-bg text-white border-0"><Plus className="h-4 w-4 mr-1.5" />Record Payment</Button>
          </div>
          <div className="flex flex-wrap gap-2 items-center bg-card border border-border rounded-2xl p-3">
            <div className="relative flex-1 min-w-[180px]"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Search Client ID, name or txn ref…" className="pl-9 h-9" data-testid="pay-search" /></div>
            <Select value={pflt.method || "all"} onValueChange={(v) => setPflt({ ...pflt, method: v === "all" ? "" : v })}>
              <SelectTrigger className="w-36 h-9" data-testid="pay-f-method"><SelectValue placeholder="Method" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Methods</SelectItem>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={pflt.status || "all"} onValueChange={(v) => setPflt({ ...pflt, status: v === "all" ? "" : v })}>
              <SelectTrigger className="w-32 h-9" data-testid="pay-f-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Status</SelectItem>{PAY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={pflt.from} onChange={(e) => setPflt({ ...pflt, from: e.target.value })} className="w-36 h-9" data-testid="pay-f-from" />
            <Input type="date" value={pflt.to} onChange={(e) => setPflt({ ...pflt, to: e.target.value })} className="w-36 h-9" data-testid="pay-f-to" />
            <Button size="sm" variant="outline" className="rounded-full font-bold" data-testid="pay-f-apply" onClick={() => loadPayments()}>Apply</Button>
          </div>
          {!payments ? <LoadingRows rows={4} /> : payments.length === 0 ? <EmptyState title="No payments yet" body="Record the first client payment — it stays pending until you verify it." icon={Receipt} /> : (
            <TableShell head={["Client", "Date / Time", "Method", "Txn Ref", "Amount", "Status", "Recorded By", "Actions"]} testid="re-org-payments-table">
              {payShown.map((p) => (
                <TR key={p.id} testid={`re-org-pay-${p.id}`}>
                  <TD><p className="font-semibold text-sm">{p.member_name}</p><p className="text-[10px] text-muted-foreground font-mono">{p.client_id}</p></TD>
                  <TD><span className="text-xs">{p.payment_date} {p.payment_time}</span></TD>
                  <TD><span className="text-xs">{p.method}</span></TD>
                  <TD><span className="text-[11px] font-mono">{p.txn_ref || "—"}</span></TD>
                  <TD><span className="font-bold">{inr2(p.amount)}</span></TD>
                  <TD><StatusBadge status={p.status.toLowerCase()} /></TD>
                  <TD><span className="text-xs">{p.recorded_by?.name}</span></TD>
                  <TD>
                    <div className="flex gap-1.5 flex-wrap">
                      {p.status === "PENDING" && <>
                        <Button size="sm" variant="outline" className="rounded-full font-bold text-emerald-600" data-testid={`pay-verify-${p.id}`}
                          onClick={() => act(() => api.post(`/re/owner/org/payments/${p.id}/verify`), "Payment verified — balance updated")}>Verify</Button>
                        <Button size="sm" variant="ghost" title="Edit" data-testid={`pay-edit-${p.id}`} onClick={() => setPayForm({ ...p })}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" title="Reject" data-testid={`pay-reject-${p.id}`} onClick={() => { setNote(""); setDlg({ mode: "reject", id: p.id }); }}><X className="h-4 w-4 text-red-500" /></Button>
                      </>}
                      {p.status === "VERIFIED" && (
                        <Button size="sm" variant="ghost" title="Reverse" data-testid={`pay-reverse-${p.id}`} onClick={() => { setNote(""); setDlg({ mode: "reverse", id: p.id }); }}><Ban className="h-4 w-4 text-amber-500" /></Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}

      {tab === "statements" && (
        <div className="space-y-4" data-testid="re-org-statements">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1"><Label className="text-xs">Client</Label>
              <Select value={stmtMember} onValueChange={(v) => { setStmtMember(v); setStmt(null); loadStatement(v); }}>
                <SelectTrigger className="w-64" data-testid="stmt-member"><SelectValue placeholder="Select client…" /></SelectTrigger>
                <SelectContent>{(members || []).filter((m) => m.role === "CLIENT").map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name} ({m.client_id})</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          {stmtMember && <>
            <StatementFilters f={sf} setF={setSf} onApply={() => loadStatement()} testid="org-stmt" />
            {stmt && <>
              <div className="flex gap-2 mb-4">
                <Button variant="outline" className="rounded-full font-bold" onClick={() => printStatement(ov?.website?.name || "Real Estate", stmt.summary, stmt.rows)} data-testid="org-stmt-print"><Printer className="h-4 w-4 mr-1.5" />Print</Button>
                <Button variant="outline" className="rounded-full font-bold" onClick={downloadPdf} data-testid="org-stmt-pdf"><Download className="h-4 w-4 mr-1.5" />PDF Export</Button>
              </div>
              <StatementView rows={stmt.rows} summary={stmt.summary} testid="org-stmt-view" />
            </>}
          </>}
        </div>
      )}

      {tab === "audit" && (!logs ? <LoadingRows rows={4} /> : logs.length === 0 ? <EmptyState title="No audit entries yet" body="Administrative changes will be logged here." icon={ScrollText} /> : (
        <TableShell head={["When", "Actor", "Action", "Detail"]} testid="re-org-audit-table">
          {logs.map((l) => (
            <TR key={l.id} testid={`audit-${l.id}`}>
              <TD><span className="text-xs text-muted-foreground">{fmtDateTime(l.at)}</span></TD>
              <TD><span className="text-xs font-semibold">{l.actor?.name}</span><span className="text-[10px] text-muted-foreground ml-1">{l.actor?.role}</span></TD>
              <TD><span className="text-[11px] font-mono font-bold">{l.action}</span></TD>
              <TD><span className="text-xs">{l.detail || "—"}</span></TD>
            </TR>
          ))}
        </TableShell>
      ))}

      {/* ====== MEMBER ADD/EDIT DIALOG ====== */}
      <Dialog open={dlg?.mode === "member"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl" data-testid="re-org-member-dialog">
          <DialogHeader><DialogTitle className="font-display">{dlg?.id ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <MemberForm form={form} setForm={setForm} staff={staff} isEdit={!!dlg?.id} />
          <Button onClick={saveMember} disabled={busy} className="w-full h-11 rounded-full font-bold gradient-bg text-white border-0" data-testid="mf-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : dlg?.id ? "Save Changes" : "Create User"}</Button>
        </DialogContent>
      </Dialog>

      {/* ====== MEMBER DETAIL DIALOG ====== */}
      <Dialog open={dlg?.mode === "detail"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl" data-testid="re-org-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">User Details</DialogTitle></DialogHeader>
          {dlg?.mode === "detail" && <MemberDetail id={dlg.id} staff={staff} onReset={(id) => act(async () => {
            const pw = window.prompt("New password (min 6 chars):");
            if (!pw) return;
            await api.post(`/re/owner/org/members/${id}/reset-password`, { password: pw });
          }, "Password reset done")} />}
        </DialogContent>
      </Dialog>

      {/* ====== PAYMENT DIALOG ====== */}
      <Dialog open={!!payForm} onOpenChange={(o) => !o && setPayForm(null)}>
        <DialogContent data-testid="re-org-payment-dialog">
          <DialogHeader><DialogTitle className="font-display">{payForm?.id ? "Edit Pending Payment" : "Record Payment"}</DialogTitle></DialogHeader>
          {payForm && (
            <div className="space-y-3">
              {!payForm.id && (
                <div className="space-y-1"><Label className="text-xs">Client *</Label>
                  <Select value={payForm.member_id} onValueChange={(v) => setPayForm({ ...payForm, member_id: v })}>
                    <SelectTrigger data-testid="pf-member"><SelectValue placeholder="Select client…" /></SelectTrigger>
                    <SelectContent>{(members || []).filter((m) => m.role === "CLIENT").map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name} ({m.client_id})</SelectItem>)}</SelectContent>
                  </Select></div>)}
              <div className="grid grid-cols-2 gap-3">
                <F label="Amount (₹) *" type="number" value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} testid="pf-amount" />
                <div className="space-y-1"><Label className="text-xs">Direction</Label>
                  <Select value={payForm.direction} onValueChange={(v) => setPayForm({ ...payForm, direction: v })} disabled={!!payForm.id}>
                    <SelectTrigger data-testid="pf-direction"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="received">Received</SelectItem><SelectItem value="sent">Sent</SelectItem></SelectContent>
                  </Select></div>
                <F label="Payment Date" type="date" value={payForm.payment_date} onChange={(v) => setPayForm({ ...payForm, payment_date: v })} testid="pf-date" />
                <F label="Payment Time" type="time" value={payForm.payment_time} onChange={(v) => setPayForm({ ...payForm, payment_time: v })} testid="pf-time" />
                <div className="space-y-1"><Label className="text-xs">Method</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger data-testid="pf-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select></div>
                <F label="Transaction Ref No." value={payForm.txn_ref} onChange={(v) => setPayForm({ ...payForm, txn_ref: v })} testid="pf-txn" />
                <F label="Description" value={payForm.description} onChange={(v) => setPayForm({ ...payForm, description: v })} ph="e.g. Plot installment 2" testid="pf-description" />
              </div>
              <div className="space-y-1"><Label className="text-xs">Remarks</Label>
                <Textarea rows={2} value={payForm.remarks || ""} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} data-testid="pf-remarks" /></div>
              <p className="text-[11px] text-muted-foreground">New payments start as <b>PENDING</b> — balances update only after you verify them.</p>
              <Button onClick={savePayment} disabled={busy} className="w-full h-11 rounded-full font-bold gradient-bg text-white border-0" data-testid="pf-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Payment"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ====== REJECT / REVERSE NOTE DIALOG ====== */}
      <Dialog open={dlg?.mode === "reject" || dlg?.mode === "reverse"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="re-org-note-dialog">
          <DialogHeader><DialogTitle className="font-display capitalize">{dlg?.mode} Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Reason {dlg?.mode === "reverse" && "*"}</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-text" /></div>
            <Button className="w-full h-11 rounded-full font-bold gradient-bg text-white border-0" disabled={busy} data-testid="note-save"
              onClick={() => act(async () => { await api.post(`/re/owner/org/payments/${dlg.id}/${dlg.mode}`, { remarks: note }); setDlg(null); }, dlg?.mode === "reject" ? "Payment rejected" : "Payment reversed — balance recalculated")}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MemberDetail({ id, staff, onReset }) {
  const [d, setD] = useState(null);
  const [v, setV] = useState(0);
  useEffect(() => { api.get(`/re/owner/org/members/${id}`).then((r) => setD(r.data.member)).catch((e) => toast.error(errMsg(e))); }, [id, v]);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const Row = ({ l, v }) => v ? <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{l}</p><p className="text-sm font-semibold mt-0.5">{v}</p></div> : null;
  return (
    <div className="space-y-5 max-h-[62vh] overflow-y-auto pr-1" data-testid="member-detail">
      <div className="flex items-center gap-4">
        {d.photo_url ? <img src={d.photo_url} alt="" className="h-14 w-14 rounded-full object-cover border border-border" />
          : <div className="h-14 w-14 rounded-full gradient-bg text-white flex items-center justify-center font-extrabold text-lg">{d.full_name[0]}</div>}
        <div className="flex-1"><p className="font-display font-extrabold text-lg">{d.full_name}</p>
          <p className="text-xs text-muted-foreground">{d.role_label} · {d.email}</p></div>
        <Button size="sm" variant="outline" className="rounded-full font-bold" data-testid="detail-reset-pw" onClick={() => onReset(d.id)}><KeyRound className="h-3.5 w-3.5 mr-1" />Reset Password</Button>
      </div>
      {Object.keys(d.pending_changes?.fields || {}).length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="detail-pending">
          <p className="text-xs font-extrabold text-amber-600 mb-2">Changes awaiting your approval</p>
          {Object.entries(d.pending_changes.fields).map(([k, val]) => (
            <p key={k} className="text-xs py-0.5"><span className="font-bold capitalize">{k.replace(/_/g, " ")}:</span> {typeof val === "object" ? JSON.stringify(val) : String(val)}</p>
          ))}
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="rounded-full font-bold gradient-bg text-white border-0" data-testid="pending-approve"
              onClick={async () => { try { await api.post(`/re/owner/org/members/${d.id}/changes/approve`); toast.success("Changes approved"); setV(v + 1); } catch (e) { toast.error(errMsg(e)); } }}>Approve</Button>
            <Button size="sm" variant="outline" className="rounded-full font-bold" data-testid="pending-reject"
              onClick={async () => { try { await api.post(`/re/owner/org/members/${d.id}/changes/reject`); toast.success("Changes rejected"); setV(v + 1); } catch (e) { toast.error(errMsg(e)); } }}>Reject</Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Row l="Client ID" v={d.client_id} /><Row l="Referral ID" v={d.referral_id} /><Row l="Rank" v={d.rank} />
        <Row l="Mobile" v={d.mobile} /><Row l="WhatsApp" v={d.whatsapp} /><Row l="District" v={d.district} />
        <Row l="Joining Date" v={d.joining_date} /><Row l="Closing Date" v={d.closing_date} />
        <Row l="Manager" v={d.manager?.full_name} />
        <Row l="Investment" v={d.role === "CLIENT" ? inr2(d.investment_amount) : ""} />
        <Row l="Verified Paid" v={d.role === "CLIENT" ? inr2(d.verified_paid) : ""} />
        <Row l="Balance" v={d.role === "CLIENT" ? inr2(d.balance) : ""} />
      </div>
      {d.address && <Row l="Address" v={d.address} />}
      <div className="rounded-xl border border-border p-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Bank & Identity (owner-only full view)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Row l="Aadhaar" v={d.aadhaar} /><Row l="Account Holder" v={d.bank?.holder} /><Row l="Account Number" v={d.bank?.account_number} />
          <Row l="Bank" v={d.bank?.bank_name} /><Row l="Branch" v={d.bank?.branch} /><Row l="IFSC" v={d.bank?.ifsc} /><Row l="MICR" v={d.bank?.micr} />
        </div>
      </div>
    </div>
  );
}
